import { memo, useMemo, useState, type ComponentProps, type JSX, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown, { type Components, type ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, Copy } from "lucide-react";
import type { IChatMention } from "@/types/(my-space)/chat.interface";
import { cn } from "@/utils/cn";
import { splitMessageText } from "../_lib/mentions";
import { hasMarkdown, rehypeFooterSlot, remarkChatText } from "../_lib/markdown";

interface MentionProps {
  isMine: boolean;
  mentions?: IChatMention[];
  myUserId?: number;
  /** Opens a direct chat with the person tagged; absent: tags are inert. */
  onMention?: (userId: number) => void;
}

// ─── Parts shared by both renderers ────────────────────────────────────────

const MessageLink = ({ href, isMine, children }: { href: string; isMine: boolean; children: ReactNode }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    className={cn("underline underline-offset-2 break-all", isMine ? "" : "text-primary")}
  >
    {children}
  </a>
);

const MentionChip = ({
  userId,
  name,
  text,
  isMine,
  myUserId,
  onMention,
}: MentionProps & { userId: number; name: string; text: string }) => {
  const { t } = useTranslation("chat");
  if (userId === myUserId) {
    return (
      <span
        title={t("mentions.you_were_mentioned")}
        className={cn(
          "rounded-sm px-1 font-semibold box-decoration-clone",
          isMine ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground",
        )}
      >
        {text}
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={!onMention}
      onClick={() => onMention?.(userId)}
      title={t("mentions.message_person", { name })}
      className={cn(
        "inline cursor-pointer rounded-xs font-semibold hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden disabled:cursor-text disabled:no-underline",
        isMine ? "underline decoration-primary-foreground/50 underline-offset-2" : "text-primary",
      )}
    >
      {text}
    </button>
  );
};

/**
 * Plain text with http(s) links made clickable and @mentions highlighted.
 * A mention opens a direct chat with that person; a mention of me is drawn
 * as a chip and goes nowhere.
 */
export const Linkified = ({ text, ...props }: MentionProps & { text: string }) => {
  const parts = splitMessageText(text, props.mentions);
  return (
    // Isolated in its own direction: English in the Arabic UI keeps its full
    // stop at the end instead of flipping to the start.
    <span dir="auto">
      {parts.map((part, i) => {
        if (part.kind === "link") {
          return (
            <MessageLink key={i} href={part.text} isMine={props.isMine}>
              {part.text}
            </MessageLink>
          );
        }
        if (part.kind === "mention") {
          return <MentionChip key={i} userId={part.userId} name={part.name} text={part.text} {...props} />;
        }
        return <span key={i}>{part.text}</span>;
      })}
    </span>
  );
};

// ─── Markdown ──────────────────────────────────────────────────────────────

const CodeBlock = ({ code, language, isMine }: { code: string; language?: string; isMine: boolean }) => {
  const { t } = useTranslation("chat");
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div
      dir="ltr"
      className={cn(
        "group/code relative my-1 overflow-hidden rounded-md border text-start",
        isMine ? "border-primary-foreground/20 bg-black/25" : "border-border bg-muted",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-2 px-2.5 py-1 text-[10px] font-medium",
          isMine ? "bg-black/15 text-primary-foreground/80" : "bg-foreground/[0.04] text-muted-foreground",
        )}
      >
        <span className="font-mono">{language || t("format.code")}</span>
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-foreground/10"
          aria-label={copied ? t("actions.copied") : t("format.copy_code")}
        >
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          {copied ? t("actions.copied") : t("actions.copy")}
        </button>
      </div>
      <pre className="max-h-80 overflow-auto px-2.5 py-2 font-mono text-xs leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
};

type El<T extends keyof JSX.IntrinsicElements> = ComponentProps<T> & ExtraProps;

/** react-markdown's own props, without its `node` (not for the DOM). */
const domProps = <T extends ExtraProps>(props: T): Omit<T, "node"> => {
  const rest = { ...props };
  delete rest.node;
  return rest;
};

const markdownComponents = (props: MentionProps, footer: ReactNode): Components => {
  const { isMine } = props;
  return {
    p: (props: El<"p">) => <p dir="auto" {...domProps(props)} />,
    h1: (props: El<"h1">) => <p dir="auto" className="text-base font-bold" {...domProps(props)} />,
    h2: (props: El<"h2">) => <p dir="auto" className="text-[15px] font-bold" {...domProps(props)} />,
    h3: (props: El<"h3">) => <p dir="auto" className="font-semibold" {...domProps(props)} />,
    h4: (props: El<"h4">) => <p dir="auto" className="font-semibold" {...domProps(props)} />,
    h5: (props: El<"h5">) => <p dir="auto" className="font-semibold" {...domProps(props)} />,
    h6: (props: El<"h6">) => <p dir="auto" className="font-semibold" {...domProps(props)} />,
    strong: (props: El<"strong">) => <strong className="font-semibold" {...domProps(props)} />,
    del: (props: El<"del">) => <del className="opacity-80" {...domProps(props)} />,
    blockquote: (props: El<"blockquote">) => (
      <blockquote
        dir="auto"
        className={cn(
          "border-s-[3px] ps-2.5 [&>*+*]:mt-1",
          isMine ? "border-primary-foreground/50 text-primary-foreground/90" : "border-primary/40 text-foreground/85",
        )}
        {...domProps(props)}
      />
    ),
    ul: (props: El<"ul">) => (
      <ul className="list-disc space-y-0.5 ps-5 marker:opacity-70 [&_ul]:list-[circle]" {...domProps(props)} />
    ),
    ol: (props: El<"ol">) => <ol className="list-decimal space-y-0.5 ps-5 marker:opacity-70" {...domProps(props)} />,
    li: (props: El<"li">) => <li dir="auto" className="[&>p]:inline" {...domProps(props)} />,
    input: (props: El<"input">) => (
      // Task-list boxes are a record of the sender's list, not a control.
      <input {...domProps(props)} disabled className="me-1 size-3 translate-y-px accent-current" />
    ),
    hr: () => <hr className={cn("my-1", isMine ? "border-primary-foreground/30" : "border-border")} />,
    a: ({ href, children }: El<"a">) =>
      href ? (
        <MessageLink href={href} isMine={isMine}>
          {children}
        </MessageLink>
      ) : (
        <>{children}</>
      ),
    // Images are never fetched from a message: shown as their link instead.
    img: ({ src, alt }: El<"img">) =>
      typeof src === "string" ? (
        <MessageLink href={src} isMine={isMine}>
          {alt || src}
        </MessageLink>
      ) : null,
    table: (props: El<"table">) => (
      <div className="my-1 max-w-full overflow-x-auto">
        <table className="w-max border-collapse text-xs [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:text-start [&_th]:font-semibold [&_td]:border-current/20 [&_th]:border-current/20" {...domProps(props)} />
      </div>
    ),
    pre: ({ node }: El<"pre">) => {
      const codeEl = node?.children.find((c) => c.type === "element" && c.tagName === "code");
      const className =
        codeEl && codeEl.type === "element" ? String((codeEl.properties.className as string[] | undefined)?.[0] ?? "") : "";
      const text =
        codeEl && codeEl.type === "element"
          ? codeEl.children.map((c) => (c.type === "text" ? c.value : "")).join("")
          : "";
      return <CodeBlock code={text.replace(/\n$/, "")} language={className.replace(/^language-/, "")} isMine={isMine} />;
    },
    code: (props: El<"code">) => (
      <code
        dir="ltr"
        className={cn(
          "rounded px-1 py-px font-mono text-[0.85em] break-words",
          isMine ? "bg-black/20" : "bg-foreground/[0.07]",
        )}
        {...domProps(props)}
      />
    ),
    span: ({ node, ...p }: El<"span">) => {
      const props_ = node?.properties ?? {};
      if ("dataFooterSlot" in props_) return <>{footer}</>;
      const mention = props_.dataMention;
      if (mention != null) {
        const userId = Number(mention);
        const text = String(p.children ?? "");
        return (
          <MentionChip userId={userId} name={text.replace(/^@/, "")} text={text} {...props} />
        );
      }
      return <span {...p} />;
    },
  };
};

/**
 * A message's text: Markdown when it has any (see `_lib/markdown.ts`), the
 * plain renderer otherwise — so ordinary messages keep their exact spacing.
 * `footer` is the bubble's reserved time gap: inline after the last line.
 */
export const MessageText = memo(function MessageText({
  text,
  footer,
  className,
  ...props
}: MentionProps & { text: string; footer?: ReactNode; className?: string }) {
  const markdown = hasMarkdown(text);
  const { isMine, mentions, myUserId, onMention } = props;
  const remarkPlugins = useMemo(() => [remarkGfm, remarkChatText(mentions)], [mentions]);
  const components = useMemo(
    () => markdownComponents({ isMine, mentions, myUserId, onMention }, footer),
    [isMine, mentions, myUserId, onMention, footer],
  );

  if (!markdown) {
    return (
      <p className={cn("whitespace-pre-wrap break-words", className)}>
        <Linkified text={text} {...props} />
        {footer}
      </p>
    );
  }
  return (
    <div className={cn("break-words [&>*+*]:mt-1.5", className)}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={footer ? [rehypeFooterSlot] : []}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});
